import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Header } from '../components/layout/Header'
import { Card } from '../components/common/Card'
import { Button } from '../components/common/Button'
import { Send, Download, Star, History, Loader2 } from 'lucide-react'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'
const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:8000/ws'

interface QueryResponse {
  id: string
  query: string
  response: string
  timestamp: string
  sources?: string[]
}

interface QueryHistory {
  id: string
  query: string
  timestamp: string
}

const SUGGESTED_QUERIES = [
  'What are the top 5 markets for electronics expansion in Europe?',
  'Show me shipping requirements for Germany',
  'Which markets have the highest growth potential for fashion?',
  'What are the customs regulations for France?',
  'Compare market opportunities: Spain vs Italy',
]

export const QueryPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [query, setQuery] = useState(searchParams.get('q') || '')
  const [response, setResponse] = useState<QueryResponse | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamedText, setStreamedText] = useState('')
  const [history, setHistory] = useState<QueryHistory[]>([])
  const [favorites, setFavorites] = useState<string[]>([])
  const wsRef = useRef<WebSocket | null>(null)
  const responseEndRef = useRef<HTMLDivElement>(null)

  // Load history and favorites from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('queryHistory')
    const savedFavorites = localStorage.getItem('queryFavorites')
    if (savedHistory) setHistory(JSON.parse(savedHistory))
    if (savedFavorites) setFavorites(JSON.parse(savedFavorites))
  }, [])

  // Auto-scroll to bottom when streaming
  useEffect(() => {
    if (isStreaming) {
      responseEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [streamedText, isStreaming])

  const handleSubmit = async (queryText?: string) => {
    const finalQuery = queryText || query
    if (!finalQuery.trim()) return

    setIsStreaming(true)
    setStreamedText('')
    setResponse(null)

    // Add to history
    const newHistoryItem: QueryHistory = {
      id: Date.now().toString(),
      query: finalQuery,
      timestamp: new Date().toISOString(),
    }
    const updatedHistory = [newHistoryItem, ...history].slice(0, 20) // Keep last 20
    setHistory(updatedHistory)
    localStorage.setItem('queryHistory', JSON.stringify(updatedHistory))

    // Connect to WebSocket for streaming response
    const ws = new WebSocket(`${WS_URL}/query`)
    wsRef.current = ws

    ws.onopen = () => {
      ws.send(JSON.stringify({ query: finalQuery }))
    }

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data)

      if (data.type === 'token') {
        setStreamedText((prev) => prev + data.content)
      } else if (data.type === 'complete') {
        setResponse({
          id: data.id,
          query: finalQuery,
          response: data.response,
          timestamp: new Date().toISOString(),
          sources: data.sources,
        })
        setIsStreaming(false)
        ws.close()
      } else if (data.type === 'error') {
        console.error('Query error:', data.error)
        setIsStreaming(false)
        ws.close()
      }
    }

    ws.onerror = (error) => {
      console.error('WebSocket error:', error)
      setIsStreaming(false)
    }

    ws.onclose = () => {
      setIsStreaming(false)
    }
  }

  const handleSaveFavorite = () => {
    if (!response) return
    const updatedFavorites = [...favorites, response.id]
    setFavorites(updatedFavorites)
    localStorage.setItem('queryFavorites', JSON.stringify(updatedFavorites))
  }

  const handleExportPDF = async () => {
    if (!response) return
    try {
      const res = await fetch(`${API_BASE_URL}/export/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(response),
      })
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `query-${response.id}.pdf`
      a.click()
    } catch (error) {
      console.error('Export error:', error)
    }
  }

  const handleExportCSV = async () => {
    if (!response) return
    const csv = `Query,Response,Timestamp\n"${response.query}","${response.response}","${response.timestamp}"`
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `query-${response.id}.csv`
    a.click()
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F5F7FA' }}>
      <Header />

      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '70% 30%', gap: '24px' }}>
          {/* Main Query Area */}
          <div>
            <Card style={{ marginBottom: '24px' }}>
              <h1 style={{ fontSize: '28px', marginBottom: '8px', color: '#1A1A1A' }}>
                AI Query Interface
              </h1>
              <p style={{ color: '#666', marginBottom: '24px' }}>
                Ask questions about market opportunities, expansion strategies, and trade data
              </p>

              {/* Query Input */}
              <div style={{ position: 'relative', marginBottom: '16px' }}>
                <textarea
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      handleSubmit()
                    }
                  }}
                  placeholder="Ask anything about market expansion opportunities..."
                  disabled={isStreaming}
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '16px',
                    border: '2px solid #E0E0E0',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'Inter, sans-serif',
                    resize: 'vertical',
                  }}
                />
                <Button
                  onClick={() => handleSubmit()}
                  disabled={!query.trim() || isStreaming}
                  style={{
                    position: 'absolute',
                    bottom: '16px',
                    right: '16px',
                  }}
                >
                  {isStreaming ? (
                    <><Loader2 className="animate-spin" style={{ marginRight: '8px' }} /> Processing...</>
                  ) : (
                    <><Send style={{ marginRight: '8px' }} /> Send</>
                  )}
                </Button>
              </div>

              <p style={{ fontSize: '12px', color: '#999' }}>
                Press Cmd/Ctrl + Enter to submit
              </p>
            </Card>

            {/* Suggested Queries */}
            {!response && !isStreaming && (
              <Card>
                <h3 style={{ fontSize: '16px', marginBottom: '16px', color: '#1A1A1A' }}>
                  Suggested Queries
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {SUGGESTED_QUERIES.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setQuery(suggestion)
                        handleSubmit(suggestion)
                      }}
                      style={{
                        textAlign: 'left',
                        padding: '12px 16px',
                        backgroundColor: '#F8F9FA',
                        border: '1px solid #E0E0E0',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#E8F4FF'
                        e.currentTarget.style.borderColor = '#4A90E2'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#F8F9FA'
                        e.currentTarget.style.borderColor = '#E0E0E0'
                      }}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </Card>
            )}

            {/* Response Area */}
            {(isStreaming || response) && (
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '18px', color: '#1A1A1A' }}>Response</h3>
                  {response && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Button onClick={handleSaveFavorite} variant="outline" size="sm">
                        <Star style={{ marginRight: '4px' }} /> Save
                      </Button>
                      <Button onClick={handleExportPDF} variant="outline" size="sm">
                        <Download style={{ marginRight: '4px' }} /> PDF
                      </Button>
                      <Button onClick={handleExportCSV} variant="outline" size="sm">
                        <Download style={{ marginRight: '4px' }} /> CSV
                      </Button>
                    </div>
                  )}
                </div>

                <div
                  style={{
                    padding: '16px',
                    backgroundColor: '#F8F9FA',
                    borderRadius: '8px',
                    border: '2px solid #7B68EE',
                    boxShadow: '0 0 20px rgba(123, 104, 238, 0.1)',
                    minHeight: '200px',
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.6',
                  }}
                >
                  {isStreaming ? streamedText : response?.response}
                  {isStreaming && (
                    <span
                      style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '16px',
                        backgroundColor: '#7B68EE',
                        marginLeft: '2px',
                        animation: 'blink 1s infinite',
                      }}
                    />
                  )}
                  <div ref={responseEndRef} />
                </div>

                {response?.sources && response.sources.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <h4 style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Sources:</h4>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      {response.sources.map((source, index) => (
                        <li key={index} style={{ fontSize: '12px', color: '#4A90E2', marginBottom: '4px' }}>
                          {source}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>
            )}
          </div>

          {/* Sidebar - History */}
          <div>
            <Card>
              <h3 style={{ fontSize: '16px', marginBottom: '16px', color: '#1A1A1A', display: 'flex', alignItems: 'center' }}>
                <History style={{ marginRight: '8px' }} /> Query History
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '600px', overflowY: 'auto' }}>
                {history.length === 0 ? (
                  <p style={{ color: '#999', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                    No queries yet
                  </p>
                ) : (
                  history.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        setQuery(item.query)
                        handleSubmit(item.query)
                      }}
                      style={{
                        textAlign: 'left',
                        padding: '12px',
                        backgroundColor: '#F8F9FA',
                        border: '1px solid #E0E0E0',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#E8F4FF'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#F8F9FA'
                      }}
                    >
                      <div style={{ fontSize: '14px', color: '#1A1A1A', marginBottom: '4px' }}>
                        {item.query.length > 60 ? item.query.slice(0, 60) + '...' : item.query}
                      </div>
                      <div style={{ fontSize: '11px', color: '#999' }}>
                        {new Date(item.timestamp).toLocaleString()}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  )
}
